// Small visual primitives ported verbatim from design_handoff/source/tile.jsx.

import { useMemo } from 'react';
import { labelForSignal } from '../lib/format.ts';
import type { SignalLabel } from '../lib/types.ts';

export function Delta({ v, size = 11 }: { v: number | null; size?: number }) {
  if (v == null)
    return (
      <span className="tile-delta flat" style={{ fontSize: size }}>
        —
      </span>
    );
  const cls = v > 0.05 ? 'up' : v < -0.05 ? 'down' : 'flat';
  const sign = v > 0 ? '+' : '';
  return (
    <span className={`tile-delta ${cls}`} style={{ fontSize: size }}>
      {cls === 'up' && <span className="tri tri-up" />}
      {cls === 'down' && <span className="tri tri-down" />}
      {cls === 'flat' && <span className="tri tri-flat" />}
      <span>
        {sign}
        {v.toFixed(1)}%
      </span>
    </span>
  );
}

interface SparklineProps {
  data: number[];
  color: string;
  width?: number;
  height?: number;
  fill?: boolean;
}

export function Sparkline({ data, color, width = 60, height = 16, fill = true }: SparklineProps) {
  const path = useMemo(() => {
    if (!data || data.length < 2) return { d: '', area: '' };
    const slice = data.slice(-48);
    const min = Math.min(...slice);
    const max = Math.max(...slice);
    const range = max - min || 1;
    const step = width / (slice.length - 1);
    let d = '';
    let area = '';
    slice.forEach((v, i) => {
      const x = i * step;
      const y = height - ((v - min) / range) * (height - 2) - 1;
      d += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)} `;
      area += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)} `;
    });
    area += `L ${width},${height} L 0,${height} Z`;
    return { d: d.trim(), area: area.trim() };
  }, [data, width, height]);
  return (
    <svg className="spark" width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      {fill && <path d={path.area} fill={color} opacity="0.18" />}
      <path
        d={path.d}
        fill="none"
        stroke={color}
        strokeWidth="1.25"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function Score({ score, signal }: { score: number; signal: SignalLabel }) {
  return (
    <span className={`score score-${signal}`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} className={`score-dot ${i < score ? 'on' : ''}`} />
      ))}
    </span>
  );
}

export function SignalPill({ signal, score }: { signal: SignalLabel; score: number }) {
  return (
    <span className={`sig sig-${signal}`}>
      {labelForSignal(signal)}
      <Score score={score} signal={signal} />
    </span>
  );
}
